import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card, Btn, Empty } from "../../components/UI";
import { FInput } from "../engagement/FormControls";
import { Wand2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function AmbiguousRow({ it, onDone }) {
  const [name, setName] = useState(it.proposed?.medication || it.current.medication);
  const [strength, setStrength] = useState(it.proposed?.strength || "");
  const resolve = async (action) => {
    try { await api.post("/medication-library/migration/resolve", { kind: it.kind, id: it.id, action, name, strength }); toast.success(action === "keep" ? "Kept as entered" : "Entry migrated"); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="rounded-xl border border-[#FFE0B2] bg-[#FFFBF3] p-3" data-testid={`medmig-ambiguous-${it.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-[#5D4037]">
        <span className="font-semibold text-[#3E2723]">{it.label}</span>
        <span>Current: <strong>{it.current.medication}</strong> {it.current.strength && `· ${it.current.strength}`}</span>
        <span className="text-[#8D5A00]">{it.reason}</span>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px_auto_auto]">
        <FInput data-testid={`medmig-name-${it.id}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Medication name" />
        <FInput data-testid={`medmig-strength-${it.id}`} value={strength} onChange={(e) => setStrength(e.target.value)} placeholder="Strength" />
        <Btn data-testid={`medmig-confirm-${it.id}`} disabled={!name.trim()} onClick={() => resolve("confirm")}>Confirm split</Btn>
        <Btn variant="ghost" data-testid={`medmig-keep-${it.id}`} onClick={() => resolve("keep")}>Keep as is</Btn>
      </div>
    </div>
  );
}

/** Find existing entries that carry the strength inside the name, apply clean splits, and confirm ambiguous ones. */
export function MedicationMigrationPanel({ onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/medication-library/migration/scan").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const applyClean = async () => {
    setBusy(true);
    try { const r = await api.post("/medication-library/migration/apply-clean"); toast.success(`${r.data.applied} entries migrated`); load(); onChanged?.(); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  if (!data) return null;
  const clean = data.items.filter((i) => i.status === "clean");
  const ambiguous = data.items.filter((i) => i.status === "ambiguous");
  return (
    <Card className="p-6" data-testid="medmig-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-[#3E2723]"><Wand2 size={16} /> Migrate existing entries</h3>
          <p className="mt-1 max-w-2xl text-xs text-[#6D4C41]">Finds library entries and chart medications where the strength sits inside the name or is mixed with directions. Clean splits apply in one step. Ambiguous rows wait for an admin to confirm, nothing is guessed.</p>
        </div>
        <Btn data-testid="medmig-apply-clean" disabled={busy || clean.length === 0} onClick={applyClean}>{busy ? "Applying…" : `Apply ${clean.length} clean split${clean.length === 1 ? "" : "s"}`}</Btn>
      </div>
      <div className="mb-3 flex gap-2 text-xs">
        <span className="flex items-center gap-1 rounded-full bg-[#E8F5E9] px-3 py-1 font-bold text-[#2E7D32]" data-testid="medmig-clean-count"><CheckCircle2 size={12} /> {data.clean} clean</span>
        <span className="flex items-center gap-1 rounded-full bg-[#FFF3E0] px-3 py-1 font-bold text-[#8D5A00]" data-testid="medmig-ambiguous-count"><AlertTriangle size={12} /> {data.ambiguous} need review</span>
      </div>
      {data.items.length === 0 ? <Empty text="Every existing entry already keeps the name and strength separate" /> : (
        <div className="space-y-2">
          {ambiguous.map((it) => <AmbiguousRow key={`${it.kind}-${it.id}`} it={it} onDone={() => { load(); onChanged?.(); }} />)}
          {clean.length > 0 && (
            <div className="max-h-64 overflow-auto rounded-xl border border-[#EFEBE0]">
              <table className="min-w-full text-xs" data-testid="medmig-clean-table">
                <thead className="sticky top-0 bg-[#FDFCF8] text-left text-[10px] font-bold uppercase text-[#7B6157]"><tr><th className="px-3 py-2">Where</th><th className="px-3 py-2">Current</th><th className="px-3 py-2">Becomes</th></tr></thead>
                <tbody>
                  {clean.map((it) => (
                    <tr key={`${it.kind}-${it.id}`} className="border-t border-[#F1EEE7]">
                      <td className="px-3 py-1.5 text-[#7B6157]">{it.label}</td>
                      <td className="px-3 py-1.5 text-[#3E2723]">{it.current.medication} <span className="text-[#7B6157]">{it.current.strength}</span></td>
                      <td className="px-3 py-1.5 font-semibold text-[#2E7D32]">{it.proposed.medication} · {it.proposed.strength}{it.merge_into ? ` (merge into ${it.merge_into})` : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
